/**
 * The continuous sounds (05 §2), from state rather than events: the amoeba's random low
 * bubbling and the magic wall's random high tinkles while they last, and the cover's steady
 * white-noise crackle while the cave dissolves in or out. Blips are scheduled ahead on the audio
 * clock like the music, so their timing does not wobble with the frame rate.
 */

import type { Bus } from './bus';
import { LOOKAHEAD } from './music/clock';
import type { Rand } from './rand';
import { amoebaBlip, magicTinkle, nextGap } from './sfx';
import type { Ambience } from './types';
import type { VoiceSpec } from './voice';

/** The cover hiss's level, and how deep its crackle (a slow random wobble of that level) goes. */
const COVER_LEVEL = 0.07;
const COVER_WOBBLE = 0.8;
/** The hiss's noise rate and the crackle's, Hz. */
const COVER_HZ = 7000;
const CRACKLE_HZ = 40;

/** Plays a blip's voices at an audio time (through the voice cap, at ambient priority). */
export type BlipOut = (voices: VoiceSpec[], at: number) => void;

interface Cover {
  readonly amp: GainNode;
  readonly sources: AudioBufferSourceNode[];
}

export class AmbienceSound {
  private flags: Ambience = { amoeba: false, magic: false, cover: false };
  private nextAmoeba = 0;
  private nextMagic = 0;
  private cover: Cover | null = null;

  constructor(
    private readonly bus: Bus,
    private readonly blip: BlipOut,
    private readonly rand: Rand,
  ) {}

  set(a: Ambience): void {
    const now = this.bus.now;
    if (a.amoeba && !this.flags.amoeba) this.nextAmoeba = now + nextGap(this.rand, 'amoeba') * 0.5;
    if (a.magic && !this.flags.magic) this.nextMagic = now + nextGap(this.rand, 'magic') * 0.5;
    if (a.cover && !this.cover) this.coverOn(now);
    if (!a.cover && this.cover) this.coverOff(now);
    this.flags = { amoeba: a.amoeba, magic: a.magic, cover: a.cover };
  }

  /** Schedule the blips due in the look-ahead window. */
  tick(): void {
    const now = this.bus.now;
    const horizon = now + LOOKAHEAD;
    if (this.flags.amoeba) {
      this.nextAmoeba = Math.max(this.nextAmoeba, now);
      while (this.nextAmoeba < horizon) {
        this.blip(amoebaBlip(this.rand), this.nextAmoeba);
        this.nextAmoeba += nextGap(this.rand, 'amoeba');
      }
    }
    if (this.flags.magic) {
      this.nextMagic = Math.max(this.nextMagic, now);
      while (this.nextMagic < horizon) {
        this.blip(magicTinkle(this.rand), this.nextMagic);
        this.nextMagic += nextGap(this.rand, 'magic');
      }
    }
  }

  /** Hiss: fast noise, high-passed, its level wobbled by a very slow noise - the crackle. */
  private coverOn(now: number): void {
    const ctx = this.bus.ctx;
    const hiss = this.noise(COVER_HZ);
    const crackle = this.noise(CRACKLE_HZ);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 1200;
    // The crackle rides on a gain of 1 before the fade, so fading out takes it down with the hiss.
    const shape = ctx.createGain();
    const wobble = ctx.createGain();
    wobble.gain.value = COVER_WOBBLE;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, now);
    amp.gain.setTargetAtTime(COVER_LEVEL, now, 0.03);
    hiss.connect(hp).connect(shape).connect(amp).connect(this.bus.sfx);
    crackle.connect(wobble).connect(shape.gain);
    hiss.start(now);
    crackle.start(now, 0.7);
    this.cover = { amp, sources: [hiss, crackle] };
    hiss.onended = () => {
      for (const n of [hiss, crackle, hp, shape, wobble, amp]) n.disconnect();
    };
  }

  private coverOff(now: number): void {
    const c = this.cover;
    if (!c) return;
    this.cover = null;
    c.amp.gain.cancelScheduledValues(now);
    c.amp.gain.setTargetAtTime(0, now, 0.04);
    for (const s of c.sources) s.stop(now + 0.25);
  }

  private noise(hz: number): AudioBufferSourceNode {
    const src = this.bus.ctx.createBufferSource();
    src.buffer = this.bus.noise;
    src.loop = true;
    src.playbackRate.value = hz / this.bus.noiseHz;
    return src;
  }
}
